import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { getCustomerCreditTransactions } from '../../services'
import { getCreditTransactionColumns } from './credit-transactions-config'

/**
 * Business logic hook for Customer Credit Transactions
 * Manages data fetching, pagination, and sorting
 */
export const useCreditTransactions = () => {
    const { id } = useParams()
    const { t } = useTranslation()

    // URL params with prefix to avoid conflicts with other tables
    const { params, setParams, searchInputProps } = useUrlSearchParams({
        defaults: { page_size: 10 },
        prefix: 'credit_', // Prefix for credit transactions table
    })

    // Form for future filter controls (if needed)
    const { control } = useForm({
        defaultValues: {},
    })

    // Fetch credit transactions
    const { data, isFetching, isError, error } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.CUSTOMERS_CREDITS,
            id,
            params.page,
            params.page_size,
            params.q,
            params.sort_by,
            params.sort_order,
        ],
        queryFn: () =>
            getCustomerCreditTransactions({
                customerID: Number(id),
                payload: {
                    page: params.page || 1,
                    page_size: params.page_size || 10,
                    q: params.q || '',
                    sort_by: params.sort_by,
                    sort_order: params.sort_order,
                },
            }),
        enabled: !!id,
    })

    // Memoized columns
    const columns = useMemo(() => getCreditTransactionColumns(t), [t])

    // Pagination handlers
    const handlePageChange = (page: number) => {
        setParams({ page })
    }

    const handlePageSizeChange = (page_size: number) => {
        setParams({ page: 1, page_size })
    }

    // Sort handler
    const handleSortChange = (sort_by: string, sort_order: 'asc' | 'desc') => {
        setParams({ sort_by, sort_order })
    }

    // Error/empty state component
    const stateComponent = useMemo(() => {
        if (isError) {
            return (
                <div className="flex items-center justify-center h-64 text-red-500">
                    {error?.message || t('error_loading_data')}
                </div>
            )
        }
        return null
    }, [isError, error, t])

    return {
        // Data
        transactions: data?.results || [],
        pagination: data?.pagination,

        // UI State
        isFetching,
        stateComponent,

        // Params
        params,

        // Columns
        columns,

        // Form control (for future filters)
        control,

        // Handlers
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,

        // Search
        searchInputProps,

        // Translation
        t,
    }
}
